import { Injectable, OnDestroy } from '@angular/core';
import { generateGUID, millisecondsToReadable } from '@functions/functions';
import { BehaviorSubject, distinctUntilChanged, Observable, Subject, takeUntil } from 'rxjs';
import { siteSubscriptions } from './siteSubscriptions';
import { GlobalFunctions, SnackType } from '@global/GlobalFunctions';

/** NotificationType */
export enum SystemMessageTypes {
	AccountExpired = 1,
	SiteMainenancePlanned = 2
}

/** NotificationType */
export enum NotificationType {
	Info = 'info',
	Warning = 'warning',
	Error = 'error',
	Success = 'success',
	System = 'system'
}

/** BannerNotification */
export interface iBannerNotification {
	notificationUID: string;          // unique ID
	accountUID: string;
	type?: NotificationType;
	id: string | number | null; // Specific ID for a message (Typically a Systems Message)
	message: string;
	timestamp: number;
	data: any | undefined;
	canSnooze: boolean;
	canCancel: boolean;
	snoozed: boolean;
	snoozedAt: number;
	snoozeUntil: number;
	snoozeMS: number;
}

/** NotificationService */
@Injectable({
	providedIn: 'root'
})
export class NotificationService implements OnDestroy {

	/** Local storage key */
	private readonly storageKey = 'notifications';

	/** Notifications List */
	private notifications: iBannerNotification[] = this.loadNotifications();

	/** BehaviourSubject so new subscribers get the current stack immediately */
	private notificationsSubject = new BehaviorSubject<iBannerNotification[]>(this.notifications);

	/** notifications$ */
	public notifications$: Observable<iBannerNotification[]> = this.notificationsSubject.asObservable();

	/** snoozeCheckInterval */
	private readonly snoozeCheckInterval = 30_000; // 30 seconds
	/** snoozeDefault */
	private readonly snoozeDefault = 60 * 60 * 1000; // 60 minutes

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** selectedAccountGUID */
	private selectedAccountGUID: string = ""

	private snoozeTimer?: ReturnType<typeof setTimeout>;

	/**
	 * Constructor
	 * @param siteSubscriptions siteSubscriptions
	 */
	constructor(
		public siteSubscriptions: siteSubscriptions,
		private globalFunctions: GlobalFunctions
	) {

		this.siteSubscriptions.selectedAccountGUID$.pipe(takeUntil(this.finalise), distinctUntilChanged())
			.subscribe({
				next: (selectedAccountGUID: string) => {
					this.selectedAccountGUID = selectedAccountGUID;
					this.applyFilter()

				}
			});

		this.scheduleSnoozeCheck();

	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
		}
		catch { }
	}

	/**
	 * scheduleSnoozeCheck
	 * @returns 
	 */
	private scheduleSnoozeCheck(): void {

		if (this.snoozeTimer) {
			clearTimeout(this.snoozeTimer);
			this.snoozeTimer = undefined;
		}

		const snoozed = this.notifications
			.filter(n => n.snoozed)
			.map(n => n.snoozeUntil)
			.filter(time => time > 0);

		if (snoozed.length === 0) {
			return;
		}

		const nextSnooze = Math.min(...snoozed);
		const delay = Math.max(0, nextSnooze - Date.now());

		this.snoozeTimer = setTimeout(() => {
			this.refreshSnoozedNotifications();
			this.scheduleSnoozeCheck();
		}, delay);
	}

	/** refreshSnoozedNotifications */
	private refreshSnoozedNotifications(): void {

		const now = Date.now();

		let changed = false;

		for (const notification of this.notifications) {
			if (
				notification.snoozed &&
				notification.snoozeUntil > 0 &&
				notification.snoozeUntil <= now
			) {
				notification.snoozed = false;
				notification.snoozedAt = 0;
				notification.snoozeUntil = 0;
				changed = true;
			}
		}

		if (!changed) { return; }

		this.saveNotifications();

		this.applyFilter()
	}

	/**
	 * Load notifications from localStorage
	 */
	private loadNotifications(): iBannerNotification[] {

		const stored = localStorage.getItem(this.storageKey);

		if (!stored) { return []; }

		try {
			return JSON.parse(stored) as iBannerNotification[];
		}
		catch (error) {
			console.error('Failed to load notifications from localStorage', error);
			return [];
		}
	}

	/**
	 * Save notifications to localStorage
	 */
	private saveNotifications(): void {
		localStorage.setItem(
			this.storageKey,
			JSON.stringify(this.notifications)
		);
	}

	/**
	 * Push a new notification into the persistent stack
	 */
	public push(
		notification:
			Omit<iBannerNotification, 'id' | 'notificationUID' | 'timestamp' | 'data' | 'snoozed' | 'snoozedAt' | 'snoozeUntil' | 'snoozeMS'> &
			Partial<Pick<iBannerNotification, 'id' | 'notificationUID' | 'timestamp' | 'data' | 'snoozed' | 'snoozedAt' | 'snoozeUntil' | 'snoozeMS'>>
	) {

		const newNotification: iBannerNotification = {
			notificationUID: notification.notificationUID ?? generateGUID(),
			accountUID: notification.accountUID,
			message: notification.message,
			type: notification.type ?? NotificationType.Info,
			timestamp: notification.timestamp ?? Date.now(),
			data: notification.data ?? undefined,
			canCancel: notification.canCancel,
			canSnooze: notification.canSnooze,
			snoozed: notification.snoozed ?? false,
			snoozedAt: notification.snoozedAt ?? 0,
			snoozeUntil: notification.snoozeUntil ?? 0,
			snoozeMS: notification.snoozeMS ?? this.snoozeDefault,
			id: notification.id ?? null

		};

		/** Find any duplicated notifications already in the list */
		const isDuplicate = this.notifications.some(n =>
			n.notificationUID === newNotification.notificationUID ||
			(
				newNotification.id &&
				n.accountUID === newNotification.accountUID &&
				n.id === newNotification.id &&
				n.type === newNotification.type
			)
		);

		if (!isDuplicate) {
			this.notifications.push(newNotification);

			this.saveNotifications();

			// this.applyFilter()
		}
		this.applyFilter()

		this.scheduleSnoozeCheck();

	}

	/**
	 * Put a new notification into the persistent stack
	 * A Put witl add if not exesting, but replace if it exists
	 */
	public put(
		notification:
			Omit<iBannerNotification, 'id' | 'notificationUID' | 'timestamp' | 'data' | 'snoozed' | 'snoozedAt' | 'snoozeUntil' | 'snoozeMS'> &
			Partial<Pick<iBannerNotification, 'id' | 'notificationUID' | 'timestamp' | 'data' | 'snoozed' | 'snoozedAt' | 'snoozeUntil' | 'snoozeMS'>>
	) {

		const newNotification: iBannerNotification = {
			notificationUID: notification.notificationUID ?? generateGUID(),
			accountUID: notification.accountUID,
			message: notification.message,
			type: notification.type ?? NotificationType.Info,
			timestamp: notification.timestamp ?? Date.now(),
			data: notification.data ?? undefined,
			canCancel: notification.canCancel,
			canSnooze: notification.canSnooze,
			snoozed: notification.snoozed ?? false,
			snoozedAt: notification.snoozedAt ?? 0,
			snoozeUntil: notification.snoozeUntil ?? 0,
			snoozeMS: notification.snoozeMS ?? this.snoozeDefault,
			id: notification.id ?? null

		};

		/** Find any duplicated notifications already in the list */
		const existingNotification = this.notifications.find(n =>
			n.notificationUID === newNotification.notificationUID ||
			(
				newNotification.id &&
				n.accountUID === newNotification.accountUID &&
				n.id === newNotification.id &&
				n.type === newNotification.type
			)
		);

		/** Ensure the snoozed notification is retained */
		if(existingNotification?.snoozed){
			newNotification.snoozed = existingNotification.snoozed;
			newNotification.snoozeUntil = existingNotification.snoozeUntil;
			newNotification.snoozedAt = existingNotification.snoozedAt;
		}

		if (existingNotification) {
			this.notifications = this.notifications.filter(n => n.notificationUID != existingNotification.notificationUID)
		}

		this.notifications.push(newNotification);
		this.saveNotifications();

		this.applyFilter()

		this.scheduleSnoozeCheck();

	}

	/**
	 * Remove a notification by ID
	 */
	public remove(notificationUID: string) {

		this.notifications = this.notifications.filter(n => n.notificationUID !== notificationUID);

		this.saveNotifications();

		this.applyFilter()
	}

	/**
	 * Snooze a notification by ID
	 */
	public snooze(notificationUID: string, durationMs: number = 0) {

		const n = this.notifications.find(n => n.notificationUID === notificationUID);

		if (!n) {
			return;
		}

		if (durationMs == 0) { durationMs = n.snoozeMS; }

		n.snoozed = true;
		n.snoozedAt = Date.now();
		n.snoozeMS = durationMs;
		n.snoozeUntil = n.snoozedAt + durationMs;

		this.globalFunctions.DisplaySnackBar({
			message: `Notification Snoozed for ${millisecondsToReadable(durationMs)}`,
			type: SnackType.Info,
			action: ""
		})

		this.saveNotifications();

		this.applyFilter()

		this.scheduleSnoozeCheck();

	}

	/**
	 * Clear all notifications
	 */
	public clear() {

		this.notifications = [];

		this.saveNotifications();

		this.notificationsSubject.next([]);
	}

	/**
	 * postInfo - Convenience methods
	 */
	public postInfo(accountUID: string, id: string | number | null = null, message: string, canCancel: boolean = true, canSnooze: boolean = true, snoozeMS: number = this.snoozeDefault) {
		this.push({ accountUID: accountUID, id: id, message: message, type: NotificationType.Info, canCancel: canCancel, canSnooze: canSnooze, snoozeMS: snoozeMS });
	}

	/**
	 * postSuccess - Convenience methods
	 */
	public postSuccess(accountUID: string, id: string | number | null = null, message: string, canCancel: boolean = true, canSnooze: boolean = true, snoozeMS: number = this.snoozeDefault) {
		this.push({ accountUID: accountUID, id: id, message: message, type: NotificationType.Success, canCancel: canCancel, canSnooze: canSnooze, snoozeMS: snoozeMS });
	}

	/**
	 * postWarning - Convenience methods
	 */
	public postWarning(accountUID: string, id: string | number | null = null, message: string, canCancel: boolean = true, canSnooze: boolean = true, snoozeMS: number = this.snoozeDefault) {
		this.push({ accountUID: accountUID, id: id, message: message, type: NotificationType.Warning, canCancel: canCancel, canSnooze: canSnooze, snoozeMS: snoozeMS });
	}

	/**
	 * postError - Convenience methods
	 */
	public postError(accountUID: string, id: string | number | null = null, message: string, canCancel: boolean = true, canSnooze: boolean = true, snoozeMS: number = this.snoozeDefault) {
		this.push({ accountUID: accountUID, id: id, message: message, type: NotificationType.Error, canCancel: canCancel, canSnooze: canSnooze, snoozeMS: snoozeMS });
	}

	/**
	 * postSystem - Convenience methods
	 */
	public postSystem(accountUID: string, id: string | number | null = null, message: string, canCancel: boolean = true, canSnooze: boolean = true, snoozeMS: number = this.snoozeDefault) {
		this.push({ accountUID: accountUID, id: id, message: message, type: NotificationType.System, canCancel: canCancel, canSnooze: canSnooze, snoozeMS: snoozeMS });
	}

	/**
	 * putSystem - Convenience methods
	 */
	public putSystem(accountUID: string, id: string | number | null = null, message: string, canCancel: boolean = true, canSnooze: boolean = true, snoozeMS: number = this.snoozeDefault) {
		this.put({ accountUID: accountUID, id: id, message: message, type: NotificationType.System, canCancel: canCancel, canSnooze: canSnooze, snoozeMS: snoozeMS });
	}

	/** applyFilter */
	private applyFilter() {
		var nList = [...this.notifications.filter(n => n.snoozed == false && (n.accountUID === "" || n.accountUID == this.selectedAccountGUID))];
		this.notificationsSubject.next(nList);
	}

	/** clean */
	cleanSystemNotifications(type: SystemMessageTypes) {
		const originalLength = this.notifications.length;
		this.notifications = this.notifications.filter(n => n.id !== type)
		if (this.notifications.length !== originalLength) {
			this.saveNotifications();
			this.applyFilter();
		}
	}

	/** clean */
	cleanAccountNotifications(accountUID: string = this.selectedAccountGUID) {
		const account = accountUID.toLowerCase();
		const originalLength = this.notifications.length;

		this.notifications = this.notifications.filter(n =>
			n.accountUID === "" ||
			n.accountUID.toLowerCase() !== account
		);

		if (this.notifications.length !== originalLength) {
			this.saveNotifications();
			this.applyFilter();
		}
	}
}